/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useRef } from 'react';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const TABS_SCROLL_CONTAINER_STYLE = css`
  label: TABS_SCROLL_CONTAINER_STYLE;
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  flex: 1;
  gap: 4px;
  align-items: flex-end;
  height: 50px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none;
  }

  scrollbar-width: none;
`;

const NAV_BUTTONS_CONTAINER_STYLE = css`
  label: NAV_BUTTONS_CONTAINER_STYLE;
  padding-top: 10px;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
`;

const NAV_BUTTON_STYLE = css`
  label: NAV_BUTTON_STYLE;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: ${COLORS2.alwaysDark.green};
  cursor: pointer;
  flex-shrink: 0;
`;

const TAB_STYLE = css`
  label: TAB_STYLE;
  ${TYPOGRAPHY2.xSmall};
  padding: ${LAYOUT2.spacing1p5}px ${LAYOUT2.spacing2p5}px;
  background: ${COLORS2.alwaysDark.background.light};
  color: ${COLORS2.alwaysDark.text.white};
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  position: relative;
  height: 35px;
  display: flex;
  align-items: center;

  &:hover {
    background: #30803d;
    color: ${COLORS2.alwaysDark.text.white};
  }
`;

const TAB_ACTIVE_STYLE = css`
  label: TAB_ACTIVE_STYLE;
  background: ${COLORS2.alwaysDark.green};
  color: ${COLORS2.alwaysDark.text.black};
  z-index: 1;

  &:hover {
    background: ${COLORS2.alwaysDark.green};
    color: ${COLORS2.alwaysDark.text.black};
  }
`;

export const ScrollableTabBar = ({
  items,
  value,
  onChange,
}: {
  items: Array<{ label: string; value: string }>;
  value: string;
  onChange: (value: string) => void;
}) => {
  const tabsScrollContainerRef = useRef<HTMLDivElement>(null);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabsScrollContainerRef.current) {
      const scrollAmount = tabsScrollContainerRef.current.clientWidth / 2;
      tabsScrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      <div ref={tabsScrollContainerRef} css={TABS_SCROLL_CONTAINER_STYLE}>
        {items.map(item => (
          <div
            key={item.value}
            css={[TAB_STYLE, value === item.value && TAB_ACTIVE_STYLE]}
            onClick={() => onChange(item.value)}
          >
            {item.label}
          </div>
        ))}
      </div>
      <div css={NAV_BUTTONS_CONTAINER_STYLE}>
        <div css={NAV_BUTTON_STYLE} onClick={() => scrollTabs('left')}>
          <svg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='30' height='30' transform='translate(30 30) rotate(-180)' fill={COLORS2.alwaysDark.green} />
            <path d='M17.9 9L15 12L12.1 15L15 18L17.9 21' stroke={COLORS2.alwaysDark.text.black} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </div>
        <div css={NAV_BUTTON_STYLE} onClick={() => scrollTabs('right')}>
          <svg width='30' height='30' viewBox='0 0 30 30' fill='none' xmlns='http://www.w3.org/2000/svg'>
            <rect width='30' height='30' fill={COLORS2.alwaysDark.green} />
            <path d='M12.1 21L15 18L17.9 15L15 12L12.1 9' stroke={COLORS2.alwaysDark.text.black} strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
          </svg>
        </div>
      </div>
    </>
  );
};
